import { api, apiRequest } from "@/lib/api";

export interface ConsultationRecordingsListParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: "ready" | "processing" | "partial" | "failed";
  provider_id?: string;
  date_from?: string;
  date_to?: string;
  sort_by?:
    | "start_time"
    | "patient_name"
    | "provider_name"
    | "segment_count"
    | "total_duration"
    | "status";
  sort_order?: "asc" | "desc";
}

export interface ConsultationRecordingListItemApi {
  id: string;
  consultation_id: string;
  date: string | null;
  start_time: string | null;
  patient_name: string;
  provider_name: string;
  provider_type: string;
  segment_count: number;
  total_duration_seconds: number;
  status: "ready" | "processing" | "partial" | "failed";
}

export interface ConsultationRecordingListResponseApi {
  data: ConsultationRecordingListItemApi[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
  stats: {
    total_recordings: number;
    this_week_count: number;
    this_week_delta_pct: number;
    avg_duration_seconds: number;
    failed_count: number;
  };
}

export interface ConsultationRecordingSegmentApi {
  segment_number: number;
  start_time: string | null;
  end_time: string | null;
  duration_seconds: number;
  status:
    | "start_requested"
    | "recording"
    | "stop_requested"
    | "uploading"
    | "ready"
    | "failed";
  gap_before_seconds: number | null;
}

export interface ConsultationRecordingPlaybackApi {
  status: string;
  url: string;
  content_type: string;
  expires_at: string | null;
}

export interface ConsultationRecordingTranscriptApi {
  id: string;
  appointment_id: string;
  recording_id: string;
  status: string;
  provider_transcript_text: string | null;
  patient_transcript_text: string | null;
  final_transcript_text: string | null;
  language: string | null;
  duration_seconds: number | null;
  last_error: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ConsultationRecordingReprocessApi {
  id: string;
  appointment_id: string;
  status: "ready" | "processing" | "partial" | "failed";
  segment_count: number;
  total_duration_seconds: number;
  started_at: string | null;
  ended_at: string | null;
  stitched_audio_status: string;
  stitched_audio_path: string;
  last_error: string;
  transcript_status: string;
  transcript_deepgram_request_id: string;
  finalization_retry_count: number;
  finalization_last_retry_at: string | null;
  finalization_last_error: string;
  finalization_last_stage: string;
  finalization_last_signature: string;
}

export const consultationRecordingsAPI = {
  list: (params: ConsultationRecordingsListParams) =>
    apiRequest<ConsultationRecordingListResponseApi>(() =>
      api.get("/echo/consultation-recordings/", { params })
    ),

  getSegments: (recordingId: string) =>
    apiRequest<ConsultationRecordingSegmentApi[]>(() =>
      api.get(`/echo/consultation-recordings/${recordingId}/segments/`)
    ),

  getPlayback: (recordingId: string) =>
    apiRequest<ConsultationRecordingPlaybackApi>(() =>
      api.get(`/echo/consultation-recordings/${recordingId}/playback/`)
    ),

  getTranscript: (recordingId: string) =>
    apiRequest<ConsultationRecordingTranscriptApi>(() =>
      api.get(`/echo/consultation-recordings/${recordingId}/transcript/`)
    ),

  reprocess: (recordingId: string) =>
    apiRequest<ConsultationRecordingReprocessApi>(() =>
      api.post(`/echo/consultation-recordings/${recordingId}/reprocess/`, {})
    ),
};
